/**
 * UPT-2124 J4a (spec `web/SPEC-UPT-2124-J4.md` §2 n° 3) — l'ÉTAT du FANTÔME : chaque geste est montré avant d'être fait. L'élément visé est encadré
 * d'un liseré accent avec une étiquette (« Géode : cliquer sur Enregistrer ») pendant 900 ms ; un bouton « Annuler » et la touche `Échap` l'arrêtent
 * (le geste n'est alors pas joué). Ce module ne dessine rien : `UiGhost.tsx` rend l'état ; la logique (délai, annulation, `Échap`) vit ici pour que
 * l'acteur et les tests n'aient pas besoin d'un composant.
 *
 * Deux modes : `countdown` (le geste suit dans 900 ms, annulable) et `highlight` (mise en évidence seule : geste interdit à la voix, confirmation en
 * attente — pas de minuterie de geste, pas de bouton).
 *
 * Mémoire seule, aucun stockage.
 */

/** Durée du fantôme avant le geste. */
export const UI_GHOST_MS = 900;
/** Durée de la mise en évidence d'un geste interdit à la voix. */
export const UI_HIGHLIGHT_MS = 3500;

export type UiGhostMode = 'countdown' | 'highlight';

export interface UiGhostState {
  id: number;
  mode: UiGhostMode;
  /** L'étiquette déjà traduite (« Géode : cliquer sur Enregistrer »). */
  label: string;
  /** L'élément visé ; `null` : étiquette seule (navigation, retour, défilement). */
  element: HTMLElement | null;
  /** Le bouton « Annuler » est montré (mode `countdown`). */
  cancellable: boolean;
}

export type UiGhostOutcome = 'done' | 'cancelled';

type Listener = () => void;

let current: UiGhostState | null = null;
let sequence = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let settle: ((outcome: UiGhostOutcome) => void) | null = null;
let removeKeyListener: (() => void) | null = null;
const listeners = new Set<Listener>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const teardown = (): void => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  removeKeyListener?.();
  removeKeyListener = null;
};

/** Efface le fantôme en cours ; le geste d'un fantôme `countdown` encore en attente est annulé. */
export const clearUiGhost = (outcome: UiGhostOutcome = 'cancelled'): void => {
  const pending = settle;
  settle = null;
  teardown();
  if (current) {
    current = null;
    notify();
  }
  pending?.(outcome);
};

/** `Échap` (ou le bouton « Annuler ») pendant le fantôme : le geste n'est pas joué. */
export const cancelUiGhost = (): boolean => {
  if (!current || current.mode !== 'countdown') {
    return false;
  }
  clearUiGhost('cancelled');
  return true;
};

export const uiGhostState = (): UiGhostState | null => current;

export const subscribeUiGhost = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const scrollToTarget = (element: HTMLElement | null): void => {
  if (element && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'center', inline: 'nearest' });
  }
};

/**
 * Montre le fantôme puis rend `done` après `ms` (le geste peut être joué) ou `cancelled` (bouton, `Échap`, un autre fantôme). Un fantôme déjà en
 * cours est annulé d'abord.
 */
export const playUiGhost = (params: { label: string; element?: HTMLElement | null; ms?: number }): Promise<UiGhostOutcome> => {
  clearUiGhost('cancelled');
  const element = params.element ?? null;
  scrollToTarget(element);
  sequence += 1;
  current = { id: sequence, mode: 'countdown', label: params.label, element, cancellable: true };
  notify();
  return new Promise<UiGhostOutcome>((resolve) => {
    settle = resolve;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancelUiGhost();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    removeKeyListener = () => document.removeEventListener('keydown', onKeyDown, true);
    timer = setTimeout(() => clearUiGhost('done'), params.ms ?? UI_GHOST_MS);
  });
};

/**
 * Met un élément en évidence (geste interdit, confirmation en attente) : `ms` (3,5 s) puis effacé ; `ms: 0` = jusqu'à `clearUiGhost`. Rend la
 * fonction qui l'efface.
 */
export const showUiHighlight = (params: { label: string; element?: HTMLElement | null; ms?: number }): (() => void) => {
  clearUiGhost('cancelled');
  const element = params.element ?? null;
  scrollToTarget(element);
  sequence += 1;
  const mine = sequence;
  current = { id: mine, mode: 'highlight', label: params.label, element, cancellable: false };
  notify();
  const ms = params.ms ?? UI_HIGHLIGHT_MS;
  if (ms > 0) {
    timer = setTimeout(() => {
      if (current?.id === mine) {
        clearUiGhost('done');
      }
    }, ms);
  }
  return () => {
    if (current?.id === mine) {
      clearUiGhost('done');
    }
  };
};
